/*
 * Adorix wallet engraving.
 *
 * Picks up to four positions. The first N are free (set in the theme editor);
 * every extra one adds a quantity of the fee product to the same cart request —
 * the same shape the NFC page uses, so orders read consistently.
 *
 * Takes over the Add to cart button only while engraving is switched on, because
 * file uploads and the extra fee line cannot go through Ella's JSON serialiser.
 */
(function () {
  function init(root) {
    if (!root || root.dataset.adxpEngReady === '1') return;
    root.dataset.adxpEngReady = '1';

    var freeCount = parseInt(root.dataset.freeCount, 10);
    if (isNaN(freeCount)) freeCount = 1;
    var feeAmount = root.dataset.feeAmount || '';

    var toggle = root.querySelector('[data-adxp-eng-toggle]');
    var panel = root.querySelector('[data-adxp-eng-panel]');
    var summary = root.querySelector('[data-adxp-eng-summary]');
    var summaryText = root.querySelector('[data-adxp-eng-summary-text]');
    var fileInput = root.querySelector('[data-adxp-eng-file]');
    var fileName = root.querySelector('[data-adxp-eng-filename]');
    var uploadFile = root.querySelector('[data-adxp-eng-upload-file]');
    var uploadThumb = root.querySelector('[data-adxp-eng-thumb]');
    var fileSize = root.querySelector('[data-adxp-eng-filesize]');
    var clearButton = root.querySelector('[data-adxp-eng-clear]');
    var replaceButton = root.querySelector('[data-adxp-eng-replace]');
    var dropZone = root.querySelector('[data-adxp-eng-dropzone]');
    // Positions the leather cannot take are still rendered, crossed out, so they
    // must be excluded here or they would count towards the free/paid tally.
    var allPositions = Array.prototype.slice.call(root.querySelectorAll('[data-adxp-eng-pos]'));
    var positions = allPositions.filter(function (el) {
      return !el.disabled && !el.classList.contains('is-off');
    });
    var preview = root.querySelector('[data-adxp-eng-preview]');
    // The add-ons are tiles like the engraving positions, so what the shopper taps
    // is a button. The hidden input beside it is what actually reaches the order,
    // and the two are kept in step by the tile's aria-pressed state.
    var addonTiles = Array.prototype.slice.call(root.querySelectorAll('[data-adxp-addon]'));
    var giftBox = root.querySelector('[data-adxp-gift]');
    var qrBox = root.querySelector('[data-adxp-qr]');
    // Disabling an input is not enough. The browser's own form serialiser skips
    // disabled fields, but the theme builds its cart request by walking
    // [name^="properties"] and reading values straight off the elements — so a
    // switched-off font still reached the order as "Position Four - font".
    // Taking the name away leaves nothing for any serialiser to find.
    function setSubmittable(el, on) {
      if (!el) return;
      el.disabled = !on;
      if (on) {
        var stored = el.getAttribute('data-adxp-name');
        if (stored && !el.getAttribute('name')) el.setAttribute('name', stored);
      } else if (el.getAttribute('name')) {
        el.setAttribute('data-adxp-name', el.getAttribute('name'));
        el.removeAttribute('name');
      }
    }

    function addonTile(kind) {
      return addonTiles.filter(function (t) {
        return t.getAttribute('data-adxp-addon') === kind;
      })[0];
    }

    function addonOn(kind) {
      var tile = addonTile(kind);
      return !!tile && tile.getAttribute('aria-pressed') === 'true';
    }

    function setAddon(kind, on) {
      var tile = addonTile(kind);
      if (!tile) return;
      tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      tile.classList.toggle('is-on', on);

      // The price appears on the tile only once it is picked, the same as an
      // engraving position — a tile that shows a charge before you have chosen
      // it reads as something you are already paying for.
      var fee = tile.querySelector('[data-addon-fee]');
      if (fee) fee.textContent = on ? tile.getAttribute('data-addon-price') || '' : '';

      var box = kind === 'gift' ? giftBox : qrBox;
      // Disabled inputs are never submitted, which is what keeps an add-on the
      // shopper turned back off from being charged for anyway.
      setSubmittable(box, on);
    }


    // Shopify's dynamic checkout button posts the form straight to checkout, which
    // skips the code that attaches the engraving details and the position fees.
    // While engraving is switched on it has to be out of reach.
    var form = root.closest('form');
    var dynamicCheckout = form ? form.querySelector('.adxp-dynamic') : null;
    var cartNote = root.querySelector('[data-adxp-eng-cart-note]');
    var buyNowButton = form ? form.querySelector('[data-adxp-eng-buynow]') : null;

    var MAX_LINES = 3;

    // The writable face of the card case is roughly a fifth of the mockup tall,
    // between its top edge and the blue card below.
    var MAX_HEIGHT = 0.2;

    // Three lines is what fits a card case face; anything past that is dropped so
    // the preview and the engraver see the same thing.
    function limitLines(value) {
      return value
        .split('\n')
        .map(function (line) { return line.trim(); })
        .filter(function (line, index) { return line !== '' || index < MAX_LINES; })
        .slice(0, MAX_LINES)
        .join('\n');
    }

    function paintPreview() {
      if (!preview) return;
      var anyOn = false;

      positions.forEach(function (pos) {
        var index = pos.getAttribute('data-adxp-eng-pos');
        var mark = root.querySelector('[data-adxp-eng-mark="' + index + '"]');
        if (!mark) return;

        var on = pos.getAttribute('aria-pressed') === 'true';
        var f = fieldsFor(index);
        var text = on && f.text ? f.text.value.trim() : '';

        if (on) anyOn = true;

        if (text) {
          var multiline = f.text && f.text.hasAttribute('data-adxp-eng-multiline');
          mark.textContent = multiline ? limitLines(text) : text;
          mark.classList.toggle('adxp-eng__mark--multi', !!multiline);
          mark.style.fontFamily = f.font ? "'" + f.font.value + "', serif" : 'serif';
          mark.removeAttribute('hidden');

          // Measure rather than guess from character count: keep the full size
          // while the engraving still fits its surface, and only shrink past that.
          mark.style.setProperty('--adxp-scale', 1);
          var stage = mark.parentElement;
          var maxw = (parseFloat(mark.getAttribute('data-maxw')) || 30) / 100;

          if (multiline) {
            // Wrap inside the engraving area rather than across the whole photo,
            // then shrink on whichever axis runs out of room first.
            mark.style.width = maxw * 100 + '%';

            var allowedW = stage ? stage.clientWidth * maxw : 0;
            var allowedH = stage ? stage.clientHeight * MAX_HEIGHT : 0;
            var scale = 1;

            if (allowedH > 0 && mark.offsetHeight > allowedH) {
              scale = allowedH / mark.offsetHeight;
            }
            // scrollWidth catches a word too long to break, which overflows the
            // fixed width instead of wrapping.
            if (allowedW > 0 && mark.scrollWidth > allowedW) {
              scale = Math.min(scale, allowedW / mark.scrollWidth);
            }
            if (scale < 1) mark.style.setProperty('--adxp-scale', Math.max(0.4, scale));
          } else {
            mark.style.width = '';
            var allowed = stage ? stage.clientWidth * maxw : 0;
            var actual = mark.offsetWidth;
            if (allowed > 0 && actual > allowed) {
              mark.style.setProperty('--adxp-scale', Math.max(0.4, allowed / actual));
            }
          }
        } else {
          mark.setAttribute('hidden', '');
        }
      });

      if (anyOn) preview.removeAttribute('hidden');
      else preview.setAttribute('hidden', '');
    }

    if (!toggle || !panel) return;

    function selected() {
      return positions.filter(function (p) { return p.getAttribute('aria-pressed') === 'true'; });
    }

    function fieldsFor(index) {
      return {
        group: root.querySelector('[data-adxp-eng-group="' + index + '"]'),
        text: root.querySelector('[data-adxp-eng-text="' + index + '"]'),
        font: root.querySelector('[data-adxp-eng-font="' + index + '"]'),
        charge: root.querySelector('[data-adxp-eng-charge="' + index + '"]'),
        groupFee: root.querySelector('[data-adxp-eng-group-fee="' + index + '"]'),
        count: root.querySelector('[data-adxp-eng-count="' + index + '"]')
      };
    }

    function updateCount(index) {
      var f = fieldsFor(index);
      if (!f.count || !f.text) return;
      var max = parseInt(f.text.getAttribute('maxlength'), 10) || 0;
      var used = f.text.value.length;
      f.count.textContent = max ? used + ' / ' + max : String(used);
      f.count.classList.toggle('is-full', max > 0 && used >= max);
    }

    // Positions are charged in the order they appear, so the free allowance always
    // covers the first ones and the shopper is never charged more than they must be.
    function ordered() {
      return selected().sort(function (a, b) {
        return Number(a.getAttribute('data-adxp-eng-pos')) - Number(b.getAttribute('data-adxp-eng-pos'));
      });
    }

    function paidPositions() {
      return ordered().slice(freeCount);
    }

    function paidCount() {
      return paidPositions().length;
    }

    function refresh() {
      var picked = selected();

      // Every position, not just the selectable ones. A position the leather
      // cannot take is left out of `positions`, so nothing here ever switched
      // its inputs off — and its font select rode along on the order as
      // "Position Four - font" for a customer who only ever picked position one.
      allPositions.forEach(function (pos) {
        var on =
          positions.indexOf(pos) !== -1 && pos.getAttribute('aria-pressed') === 'true';
        var f = fieldsFor(pos.getAttribute('data-adxp-eng-pos'));
        if (f.group) {
          if (on) f.group.removeAttribute('hidden');
          else f.group.setAttribute('hidden', '');
        }
        // Disabled inputs are never submitted, which keeps empty properties off the order.
        setSubmittable(f.text, on);
        setSubmittable(f.font, on);
        var isFree = paidPositions().indexOf(pos) === -1;
        var priceLabel = isFree ? 'Free' : '+' + (pos.getAttribute('data-fee-price') || feeAmount);

        if (f.charge) {
          setSubmittable(f.charge, on);
          f.charge.value = on ? (isFree ? 'Free' : pos.getAttribute('data-fee-price') || feeAmount) : '';
        }

        if (f.groupFee) {
          f.groupFee.textContent = on ? priceLabel : '';
          f.groupFee.classList.toggle('is-free', isFree);
        }

        if (on) updateCount(pos.getAttribute('data-adxp-eng-pos'));
      });

      // Keep the add-on inputs in step on every pass, not only when a tile is
      // tapped. Rendered with their names already on them, they were picked up
      // by the theme's serialiser on a plain add — so an order came through with
      // "Gift wrap: Yes" and "QR code: Yes" for a shopper who chose neither.
      setSubmittable(giftBox, addonOn('gift'));
      setSubmittable(qrBox, addonOn('qr'));

      // The upload lives inside one position's card, so it follows that card's
      // own selection rather than the whole panel.
      if (fileInput) {
        var owner = fileInput.closest('[data-adxp-eng-group]');
        var ownerOn = owner ? owner.hasAttribute('hidden') === false : false;
        fileInput.disabled = !ownerOn;
        if (!ownerOn && fileInput.value) clearFile();
      }

      // Buy now stays available: the engraving fields are real form inputs, so a
      // native submit carries them. It only steps aside when money has to be added
      // — a paid position or a file upload needs the cart request we build here,
      // and a product form can only ever post one variant.
      // One Buy it now is always on screen: Shopify's own when it can carry the
      // order, ours when the order needs a fee line or a file as well.
      // Gift wrap is a second cart line, and the express button can only ever post
      // one variant — so ticking it moves the order onto our own Add to cart, the
      // same as a paid position or a file upload does.
      // NFC Tap belongs in this list for the same reason as the rest: it adds a
      // second cart line. Left out, the express button stayed on screen, and
      // going through it carried the choice as a property while quietly losing
      // the charge — the order read "with NFC Tap" and was never billed for it.
      var needsCart =
        paidPositions().length > 0 ||
        addonOn('gift') ||
        addonOn('qr') ||
        !!nfcFeeVariant() ||
        (fileInput && fileInput.files && fileInput.files.length > 0);

      // The link field only exists while the QR is wanted, and a disabled input is
      // never submitted — so an unticked QR cannot leave a stray link on the order.
      if (dynamicCheckout) dynamicCheckout.hidden = !!needsCart;
      if (buyNowButton) buyNowButton.hidden = !needsCart;
      if (cartNote) cartNote.hidden = true;

      // Label each selected position Free or with its own price, in charge order.
      var paidList = paidPositions();
      positions.forEach(function (pos) {
        var badge = pos.querySelector('[data-adxp-eng-pos-fee]');
        if (!badge) return;
        if (pos.getAttribute('aria-pressed') !== 'true') {
          badge.textContent = '';
          badge.classList.remove('is-free');
          return;
        }
        var isFree = paidList.indexOf(pos) === -1;
        badge.textContent = isFree ? 'Free' : '+' + (pos.getAttribute('data-fee-price') || feeAmount);
        badge.classList.toggle('is-free', isFree);
      });

      if (summary && summaryText) {
        if (!picked.length) {
          summary.setAttribute('hidden', '');
        } else {
          summary.removeAttribute('hidden');
          var parts = [picked.length + (picked.length === 1 ? ' position' : ' positions') + ' selected'];
          if (paidList.length) {
            parts.push(
              paidList.length +
                ' paid — ' +
                paidList
                  .map(function (p) { return p.getAttribute('data-fee-price') || feeAmount; })
                  .join(' + ') +
                ' added at checkout'
            );
          } else {
            parts.push('no extra charge');
          }
          summaryText.textContent = parts.join(' — ');
        }
      }

      paintPreview();
    }

    // Typing and font changes repaint the mockup straight away.
    root.querySelectorAll('[data-adxp-eng-text]').forEach(function (input) {
      input.addEventListener('input', function () {
        updateCount(input.getAttribute('data-adxp-eng-text'));
        paintPreview();
      });
    });
    root.querySelectorAll('[data-adxp-eng-font]').forEach(function (select) {
      select.addEventListener('change', paintPreview);
    });

    toggle.addEventListener('change', function () {
      // Once the shopper has touched it they know it is there — no more nudging,
      // even if they switch it back off.
      root.classList.add('adxp-eng--touched');

      if (toggle.checked) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
        positions.forEach(function (p) {
          p.setAttribute('aria-pressed', 'false');
          p.classList.remove('is-on');
        });
        // A ticked box inside a hidden panel is still a ticked box, and would be
        // charged for a wrap the shopper thinks they turned off.
        // An add-on left on inside a closed panel would still be charged.
        setAddon('gift', false);
        setAddon('qr', false);
        clearFile();
      }
      refresh();
    });

    positions.forEach(function (pos) {
      pos.addEventListener('click', function () {
        var on = pos.getAttribute('aria-pressed') === 'true';
        pos.setAttribute('aria-pressed', on ? 'false' : 'true');
        pos.classList.toggle('is-on', !on);
        refresh();
      });
    });

    function prettySize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    function clearFile() {
      if (!fileInput) return;
      fileInput.value = '';
      if (uploadFile) uploadFile.setAttribute('hidden', '');
      if (dropZone) dropZone.removeAttribute('hidden');
      if (fileName) fileName.textContent = '';
      if (fileSize) fileSize.textContent = '';
      if (uploadThumb && uploadThumb.src && uploadThumb.src.indexOf('blob:') === 0) {
        URL.revokeObjectURL(uploadThumb.src);
        uploadThumb.removeAttribute('src');
      }
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file) {
          clearFile();
          refresh();
          return;
        }
        if (fileName) fileName.textContent = file.name;
        if (fileSize) fileSize.textContent = prettySize(file.size);
        if (uploadThumb) uploadThumb.src = URL.createObjectURL(file);
        // The preview takes the drop zone's place rather than stacking under it.
        if (uploadFile) uploadFile.removeAttribute('hidden');
        if (dropZone) dropZone.setAttribute('hidden', '');
        refresh();
      });
    }

    if (replaceButton && fileInput) {
      replaceButton.addEventListener('click', function (event) {
        event.preventDefault();
        fileInput.click();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', function (event) {
        event.preventDefault();
        clearFile();
        refresh();
      });
    }

    // The box says you can drop a file on it, so make that true.
    if (dropZone && fileInput) {
      ['dragenter', 'dragover'].forEach(function (type) {
        dropZone.addEventListener(type, function (event) {
          if (fileInput.disabled) return;
          event.preventDefault();
          dropZone.classList.add('is-dragging');
        });
      });

      ['dragleave', 'drop'].forEach(function (type) {
        dropZone.addEventListener(type, function () {
          dropZone.classList.remove('is-dragging');
        });
      });

      dropZone.addEventListener('drop', function (event) {
        if (fileInput.disabled) return;
        event.preventDefault();
        var dropped = event.dataTransfer && event.dataTransfer.files;
        if (!dropped || !dropped.length) return;
        fileInput.files = dropped;
        fileInput.dispatchEvent(new Event('change'));
      });
    }

    // Ticking gift wrap adds a cart line, which decides whether the express
    // button can still be used — so the panel has to re-read itself.
    // The NFC switch lives in the section, so it announces its change rather
    // than reaching in here — this panel decides what that means for checkout.
    document.addEventListener('adxp:addons-changed', refresh);

    addonTiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        var kind = tile.getAttribute('data-adxp-addon');
        setAddon(kind, !addonOn(kind));
        refresh();
      });
    });

    refresh();


    // ── Cart submission ───────────────────────────────────────────────────────
    var addButton = form && form.querySelector('[data-adxp-atc]');
    if (!form || !addButton) return;

    // The NFC Tap choice lives in the section, outside this panel, but it is
    // charged the same way a paid engraving position is — so it is read from
    // here rather than given a second Add to cart handler of its own. Two pieces
    // of code fighting over that button is how it broke before.
    var nfcPick = form ? form.querySelector('[data-adxp-nfcpick]') : null;

    function nfcFeeVariant() {
      if (!nfcPick) return '';
      var sw = nfcPick.querySelector('[data-adxp-nfc-toggle]');
      if (!sw || !sw.checked) return '';
      return nfcPick.getAttribute('data-nfc-fee-variant') || '';
    }

    // Take over Add to cart when the order needs more than the one variant a
    // plain form post can carry.
    function isActive() {
      var nfcToggle = nfcPick && nfcPick.querySelector('[data-adxp-nfc-toggle]');
      return !!(nfcToggle && nfcToggle.checked) ||
        addonOn('gift') || addonOn('qr') ||
        (toggle.checked && selected().length > 0);
    }

    function buildProperties() {
      // Same names the form inputs use, so an order reads identically whether it
      // came through Add to cart or Buy now. ASCII only — Shopify rejects the whole
      // cart request if a property name carries something like an em dash.
      var props = {};
      var paid = paidPositions();
      ordered().forEach(function (pos) {
        var index = pos.getAttribute('data-adxp-eng-pos');
        var f = fieldsFor(index);
        var label = pos.getAttribute('data-pos-label') || 'Position ' + index;
        var raw = f.text && f.text.value ? f.text.value.trim() : '';
        props[label + ' - text'] =
          f.text && f.text.hasAttribute('data-adxp-eng-multiline') ? limitLines(raw) : raw;
        if (f.font) props[label + ' - font'] = f.font.value;
        props[label + ' - charge'] = paid.indexOf(pos) === -1 ? 'Free' : pos.getAttribute('data-fee-price') || feeAmount;
      });
      // Carried on the item itself, not only as its own fee line, so whoever packs
      // the order sees "wrap this one" against the wallet rather than a loose
      // charge further down the order.
      // This path builds the properties itself, so the NFC choice has to be
      // copied across or it would reach the order only on a plain add.
      if (nfcPick) {
        var nfcValue = nfcPick.querySelector('[data-adxp-nfc-value]');
        if (nfcValue && nfcValue.getAttribute('name')) {
          props[propertyName(nfcValue)] = nfcValue.value;
        }
      }
      if (addonOn('gift') && giftBox) {
        props[propertyName(giftBox)] = 'Yes';
      }
      if (addonOn('qr') && qrBox) {
        props[propertyName(qrBox)] = 'Yes';
      }
      return props;
    }

    function appendProps(fd, i, props) {
      Object.keys(props).forEach(function (key) {
        fd.append('items[' + i + '][properties][' + key + ']', props[key]);
      });
    }

    // The label the shop chose in the theme editor is already in the input's name,
    // so the order and the form agree without the name being written twice.
    function propertyName(el) {
      var name = el.getAttribute('name') || el.getAttribute('data-adxp-name');
      if (!name) throw new Error('Personalization details could not be read. Please refresh and try again.');
      return name.replace(/^properties\[/, '').replace(/\]$/, '');
    }

    // Returns true when every picked position has its text, otherwise focuses the
    // first empty one and returns false.
    function textIsComplete() {
      var missing = selected().filter(function (pos) {
        var f = fieldsFor(pos.getAttribute('data-adxp-eng-pos'));
        return !f.text || !f.text.value.trim();
      });
      if (!missing.length) return true;

      var first = fieldsFor(missing[0].getAttribute('data-adxp-eng-pos')).text;
      if (first) {
        first.focus();
        first.classList.add('is-error');
        setTimeout(function () { first.classList.remove('is-error'); }, 1600);
      }
      return false;
    }

    // Builds the whole order — the wallet with its engraving properties plus one
    // fee line per paid position — and sends it in a single cart request.
    var submitting = false;

    function submitEngravedOrder(button, goToCheckout) {
        if (submitting) return;
        var variantInput = form.querySelector('[data-adxp-variant-input]');
        var qtyInput = form.querySelector('[name="quantity"]');
        var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

        var fd = new FormData();
        var expected = [];
        function addLine(index, variant, label) {
          if (!variant || !/^\d+$/.test(String(variant))) {
            throw new Error(label + ' is currently unavailable. Please refresh or contact us before ordering.');
          }
          expected.push({ id: String(variant), quantity: qty });
          fd.append('items[' + index + '][id]', variant);
          fd.append('items[' + index + '][quantity]', String(qty));
        }
        addLine(0, variantInput && variantInput.value, 'This product');
        appendProps(fd, 0, buildProperties());

        if (fileInput && fileInput.files && fileInput.files[0]) {
          fd.append('items[0][properties][Reference image]', fileInput.files[0]);
        }

        // One fee line per paid position, using that position's own product, so the
        // order lists exactly which positions were charged.
        var lineIndex = 1;
        paidPositions().forEach(function (pos) {
          var variant = pos.getAttribute('data-fee-variant');
          addLine(lineIndex, variant, pos.getAttribute('data-pos-label') || 'Engraving');
          fd.append('items[' + lineIndex + '][properties][For]', pos.getAttribute('data-pos-label') || '');
          lineIndex += 1;
        });

        // Gift wrap is one wrap per item, so it follows the quantity — two wallets
        // ordered wrapped need two wraps, not one.
        if (addonOn('gift')) {
          addLine(lineIndex, giftBox && giftBox.getAttribute('data-gift-variant'), 'Gift wrap');
          lineIndex += 1;
        }

        if (addonOn('qr')) {
          addLine(lineIndex, qrBox && qrBox.getAttribute('data-qr-variant'), 'QR code');
          lineIndex += 1;
        }

        // One NFC Tap per wallet, so it follows the quantity.
        var nfcFee = nfcFeeVariant();
        var nfcToggle = nfcPick && nfcPick.querySelector('[data-adxp-nfc-toggle]');
        if (nfcToggle && nfcToggle.checked) {
          addLine(lineIndex, nfcFee, 'NFC Tap');
          lineIndex += 1;
        }

        var label = button.dataset.adxpLabel || button.textContent.trim();
        button.dataset.adxpLabel = label;
        submitting = true;
        addButton.disabled = true;
        if (buyNowButton) buyNowButton.disabled = true;
        button.textContent = goToCheckout ? 'Taking you to checkout…' : 'Adding…';

        fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
              return data;
            });
          })
          .then(function (data) {
            var items = data && data.items;
            if (!Array.isArray(items) || expected.some(function (line) {
              return !items.some(function (item) {
                return String(item.variant_id || item.id) === line.id && Number(item.quantity) >= line.quantity;
              });
            })) {
              throw new Error('Could not confirm all personalization charges. Please check your cart before checkout.');
            }
            if (goToCheckout) {
              window.location.href = '/checkout';
              return;
            }
            button.textContent = 'Added to cart';
            button.classList.add('is-added');
            document.dispatchEvent(new CustomEvent('adxp:cart-updated'));
            fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (cart) {
                document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                  el.textContent = cart.item_count;
                });
              })
              .catch(function () {});
            setTimeout(function () {
              submitting = false;
              addButton.disabled = false;
              if (buyNowButton) buyNowButton.disabled = false;
              button.textContent = label;
              button.classList.remove('is-added');
            }, 2500);
          })
          .catch(function (err) {
            alert(err.message || 'Could not add to cart.');
            submitting = false;
            addButton.disabled = false;
            if (buyNowButton) buyNowButton.disabled = false;
            button.textContent = label;
          });
    }

    function handleSubmission(event, button, goToCheckout) {
      // Stop the plain form before validation or request construction can fail.
      // A fallback form post carries the text but loses every extra charge.
      if (!isActive()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitting) return;
      try {
        if (textIsComplete()) submitEngravedOrder(button, goToCheckout);
      } catch (err) {
        submitting = false;
        addButton.disabled = false;
        if (buyNowButton) buyNowButton.disabled = false;
        if (button.dataset.adxpLabel) button.textContent = button.dataset.adxpLabel;
        alert(err.message || 'Could not prepare personalization charges. Please refresh and try again.');
      }
    }

    addButton.addEventListener('click', function (event) {
      handleSubmission(event, addButton, false);
    }, true);

    // Keyboard submissions must use the same multi-item request as a click.
    form.addEventListener('submit', function (event) {
      handleSubmission(event, addButton, false);
    }, true);

    // Our own Buy it now, shown whenever the native express button cannot carry
    // the order — it does the same cart request, then goes straight to checkout.
    if (buyNowButton) {
      buyNowButton.addEventListener('click', function (event) {
        handleSubmission(event, buyNowButton, true);
      });
    }
  }

  // Tapping a ruled-out position answers why, rather than doing nothing.
  // Delegated from the document so it keeps working after the Theme Editor
  // re-renders the section, and bound in the CAPTURE phase because Ella's
  // product form stops click propagation before it reaches the document.
  var offTimer = null;
  function showOffNote(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var tile = target.closest('[data-adxp-eng-pos].is-off');
    if (!tile) return;
    event.preventDefault();

    var root = tile.closest('[data-adxp-eng]');
    var note = root && root.querySelector('[data-adxp-eng-offmsg]');
    if (!note) return;

    var label = tile.getAttribute('data-pos-label') || '';
    var text = note.getAttribute('data-off-message') || '';
    note.textContent = label ? label + ' — ' + text : text;
    note.hidden = false;
    note.classList.remove('is-flash');
    void note.offsetWidth; // restart the animation when the same tile is tapped twice
    note.classList.add('is-flash');

    window.clearTimeout(offTimer);
    offTimer = window.setTimeout(function () {
      note.classList.remove('is-flash');
      note.hidden = true;
    }, 5000);
  }

  document.addEventListener('click', showOffNote, true);
  // Some mobile browsers deliver a tap on an aria-disabled control as touch only.
  document.addEventListener('touchend', showOffNote, true);

  function boot() {
    document.querySelectorAll('[data-adxp-eng]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
